import {
	fetchCepAddress,
	usePostProperty,
	useUpdateAvatarProperty,
	useUpdateProperty,
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PROPERTY_FORM, PROPERTY_FIELD_LABELS } from '../constants/property.constants';
import { PropertySchema } from '../schemas/property.schema';
import type { PropertyFormData } from '../types';
import { stateToUf, ufToState } from '../utils/masks';

export interface UsePropertyFormOptions {
	initialData?: PropertyFormData;
	onSubmit: (createdId?: string) => void;
	avatarfile?: File | null;
}

export const usePropertyForm = ({ initialData, onSubmit, avatarfile }: UsePropertyFormOptions) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const toast = useToast();
	const initialForm = initialData
		? { ...DEFAULT_PROPERTY_FORM, ...initialData, state: ufToState(initialData.state) }
		: { ...DEFAULT_PROPERTY_FORM };
	const [form, setForm] = useState<PropertyFormData>(initialForm);
	const initialFormRef = useRef(initialForm);

	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isStateEditable, setIsStateEditable] = useState(true);
	const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());

	const { mutateAsync: createPropertyMutate, isPending: isCreating } = usePostProperty();
	const { mutateAsync: updatePropertyMutate, isPending: isUpdating } = useUpdateProperty();
	const { mutateAsync: updateAvatarPropertyMutate, isPending: isUpdatingAvatar } =
		useUpdateAvatarProperty();

	const isLoading = isCreating || isUpdating || isUpdatingAvatar;

	const set = <K extends keyof PropertyFormData>(key: K, value: PropertyFormData[K]) =>
		setForm((f) => ({ ...f, [key]: value }));

	const handleCepBlur = async (cep: string) => {
		if (cep.replace(/\D/g, '').length !== 8) return;
		try {
			const address = await fetchCepAddress(cep);
			setForm((f) => ({
				...f,
				street: address.street || f.street,
				district: address.district || f.district,
				city: address.city || f.city,
				state: address.state || f.state,
			}));
			setIsStateEditable(!address.state);
			const filled = (['street', 'district', 'city', 'state'] as const).filter(
				(key) => address[key]
			);
			setAutoFilledFields(new Set(filled));
			toast.success('Endereço preenchido automaticamente pelo CEP.');
			window.setTimeout(() => setAutoFilledFields(new Set()), 1200);
		} catch {
			setIsStateEditable(true);
			toast.error('CEP não encontrado. Preencha o endereço manualmente.');
		}
	};

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = PropertySchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [
				...new Set(result.error.issues.map((i) => String(i.path[0] ?? '')).filter(Boolean)),
			];
			const labels = uniquePaths.map((p) => PROPERTY_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		try {
			const payload = {
				userId: '',
				name: form.name,
				subscription: form.subscription || undefined,
				state: stateToUf(form.state),
				address: {
					city: form.city,
					state: stateToUf(form.state),
					street: form.street,
					district: form.district,
					number: form.number,
					zipCode: form.cep,
				},
				cep: form.cep,
				status: 'active' as const,
				totalSize: 0,
				avatar: avatarfile,
			};

			const idFromUrl = searchParams.get('id');

			if (idFromUrl) {
				await updatePropertyMutate({
					id: idFromUrl,
					data: payload,
				});

				if (avatarfile instanceof File) {
					try {
						await updateAvatarPropertyMutate({
							id: idFromUrl,
							avatar: avatarfile,
						});
						toast.success('Propriedade atualizada com sucesso!');
					} catch {
						toast.error('Propriedade atualizada, mas não foi possível atualizar o avatar.');
					}
				}

				onSubmit();
			} else {
				const created = await createPropertyMutate(payload);
				toast.success('Propriedade salva com sucesso!');
				if (created?._id) {
					const createdIdValue = created._id;
					setSearchParams((prev) => {
						const next = new URLSearchParams(prev);
						next.set('id', createdIdValue);
						return next;
					});
				}
				onSubmit(created?._id);
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar propriedade');
		}
	};

	return {
		form,
		errors,
		isLoading,
		isStateEditable,
		autoFilledFields,
		set,
		handleCepBlur,
		handleSubmit,
		isDirty,
	};
};
